// OptimizedModal.js

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * ✅ Performans Odaklı Modal Temel Bileşeni
 * - createPortal ile body'nin altına taşınır
 * - isOpen=false ise render edilmez
 * - ESC tuşu ile kapatma ve Body scroll kilidi ekler
 * - GPU ivmelendirme stilleri içerir
 * * @param {boolean} isOpen - Modal'ın açık olup olmadığı.
 * @param {function} onClose - Modal'ı kapatma fonksiyonu.
 * @param {React.ReactNode} children - Modal içeriği.
 * @param {string} className - Dış overlay için Tailwind sınıfları.
 */
export const OptimizedModal = ({ isOpen, onClose, children, className = '' }) => {
    const overlayRef = useRef(null);

    // ✅ ESC tuşu ile kapatma ve Body scroll'u kilitleme
    useEffect(() => {
        if (!isOpen) return;

        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden'; // ✅ Body scroll'u kilitle

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset'; // Temizle
        };
    }, [isOpen, onClose]);

    // ✅ Modal açık değilse hiç render etme
    if (!isOpen) return null;

    // ✅ Portal kullanarak modal'ı body'nin altına taşı
    return createPortal(
        <div
            ref={overlayRef}
            // Tailwind sınıfları ve özel animasyon sınıfları
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay ${className}`}
            style={{
                // ✅ GPU ACCELERATION - Overlay için
                willChange: 'opacity',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden'
            }}
            onClick={(e) => {
                // Sadece backdrop'a tıklanırsa kapat
                if (e.target === overlayRef.current) onClose();
            }}
        >
            {/* ✅ Basitleştirilmiş ve ivmelendirilmiş backdrop */}
            <div
                className="absolute inset-0 bg-black"
                style={{
                    opacity: 0.7,
                    willChange: 'opacity',
                    transition: 'opacity 0.2s ease'
                }}
            />

            {/* ✅ Modal içeriği - Animasyon sınıfı ve GPU ivmelendirme */}
            <div
                className="relative z-10 modal-content"
                style={{
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body
    );
};